import { Form, Typography } from 'antd'
import { useEffect, useRef } from 'react'
import { useWatch } from 'antd/lib/form/Form'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSelect from 'components/CIMSSelect'
import UserSelect from 'components/CIMSSelect/common/UserSelect'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { HelpMessage } from 'utils/constants'
import { usePermissionsContext } from '../../../PermissionsContext'
import {
  getPermissionActionMetadata,
  isWorkflowPermissionAction,
  permissionActionOptions,
  PermissionConditionKey,
} from '../../../metadata'
import { PermissionFormValues } from '../../../types'
import {
  CLOSED_STATUS_ID,
  CLOSED_STATUS_LABEL,
  DRAFT_STATUS_ID,
  DRAFT_STATUS_LABEL,
} from '../../../utils'

export type PermissionFormItemsProps = {
  readOnlyAction?: string
}

const conditionFields: PermissionConditionKey[] = [
  'fieldIds',
  'statusIds',
  'approvalStatusId',
]

const PermissionFormItems: React.FC<PermissionFormItemsProps> = ({
  readOnlyAction,
}) => {
  const form = Form.useFormInstance<PermissionFormValues>()
  const {
    fetching,
    metadata: { fields, statuses },
  } = usePermissionsContext()
  const watchedAction = useWatch('action', form)
  const approvalStatus = useWatch('approvalStatusId', form)
  const targetStatuses = useWatch('statusIds', form)
  const action = readOnlyAction ?? watchedAction
  const previousAction = useRef<string | undefined>()
  const metadata = getPermissionActionMetadata(action ?? '')
  const fieldOptions = useOptions({
    items: fields,
    value: 'id',
    label: 'name',
    tooltip: 'tooltip',
    deleted: ({ deleted }) => deleted,
    disabled: ({ deleted }) => deleted,
    sort: 'name',
  })
  const statusOptions = useOptions({
    items: statuses,
    value: 'id',
    label: 'name',
    tooltip: 'tooltip',
    deleted: ({ deleted }) => deleted,
    disabled: ({ deleted }) => deleted,
    sort: 'name',
  })
  const targetStatusValues = new Set(
    targetStatuses?.map(({ value }) => value) ?? []
  )
  const approvalStatusOptions = statusOptions.filter(
    ({ value }) => value == null || !targetStatusValues.has(value)
  )
  const isRevertAction = action === 'workflow_record.revert'
  let statusConditionOptions
  if (isRevertAction)
    statusConditionOptions = [
      { value: DRAFT_STATUS_ID, label: DRAFT_STATUS_LABEL },
      ...statusOptions,
    ]
  else {
    statusConditionOptions = [
      ...statusOptions.filter(({ value }) => value !== approvalStatus?.value),
      { value: CLOSED_STATUS_ID, label: CLOSED_STATUS_LABEL },
    ]
    if (action === 'sharepoint-plugin.upload_remove')
      statusConditionOptions.unshift({
        value: DRAFT_STATUS_ID,
        label: DRAFT_STATUS_LABEL,
      })
  }
  const help = HelpMessage.CustomWorkflows.Permissions
  const permissionHelp = action ? metadata.description : help.Permission

  useEffect(() => {
    if (previousAction.current && previousAction.current !== action) {
      form.resetFields(conditionFields)
    }
    previousAction.current = action
  }, [action, form])

  useEffect(() => {
    if (
      !approvalStatus ||
      !targetStatuses?.some(({ value }) => value === approvalStatus.value)
    ) {
      return
    }

    form.setFieldsValue({
      statusIds: targetStatuses.filter(
        ({ value }) => value !== approvalStatus.value
      ),
    })
  }, [approvalStatus, form, targetStatuses])

  const requires = (key: PermissionConditionKey) =>
    isWorkflowPermissionAction(action) && metadata.conditionKeys.includes(key)

  return (
    <>
      {readOnlyAction === undefined ? (
        <CIMSFormItem
          name="action"
          label="Permission"
          help={permissionHelp}
          requiredMessage="Please select a permission"
        >
          <CIMSSelect
            simple
            loading={fetching}
            options={permissionActionOptions}
            style={{ width: '100%' }}
          />
        </CIMSFormItem>
      ) : (
        <CIMSFormItem label="Permission" help={permissionHelp}>
          <Typography.Text>
            {metadata.label} - {metadata.description}
          </Typography.Text>
        </CIMSFormItem>
      )}
      {requires('fieldIds') && (
        <CIMSFormItem
          name="fieldIds"
          label="Fields"
          help={help.Fields}
          requiredMessage="Please select at least one field"
        >
          <CIMSSelect
            mode="multiple"
            allowClear={false}
            loading={fetching}
            options={fieldOptions}
            style={{ width: '100%' }}
          />
        </CIMSFormItem>
      )}
      {requires('approvalStatusId') && (
        <CIMSFormItem
          name="approvalStatusId"
          label="Approval Status"
          help={help.ApprovalStatus}
          requiredMessage="Please select an approval status"
        >
          <CIMSSelect
            loading={fetching}
            options={approvalStatusOptions}
            style={{ width: '100%' }}
          />
        </CIMSFormItem>
      )}
      {requires('statusIds') && (
        <CIMSFormItem
          name="statusIds"
          label="Statuses"
          help={help.Statuses}
          requiredMessage="Please select at least one status"
        >
          <CIMSSelect
            mode="multiple"
            allowClear={false}
            loading={fetching}
            options={statusConditionOptions}
            style={{ width: '100%' }}
          />
        </CIMSFormItem>
      )}
      <CIMSFormItem name="users" label="Users" help={help.Users}>
        <UserSelect mode="multiple" style={{ width: '100%' }} />
      </CIMSFormItem>
    </>
  )
}

export default PermissionFormItems
